import React from "react";
import { compact, omitBy } from "lodash-es";
import { casesActions } from "configs";
import {
  FormWatcherCallback,
  PartialNonNullable,
  resolveNumber,
  useCurrentCase,
  useDispatch,
  useParams
} from "duck";
import { useUpdateQuickChartMutation } from "requests";
import { Chart, Charts, FormValues, NumberFields } from "./types";

const fieldsToResolve: NumberFields = [
  "periodontalPocketMBuc",
  "periodontalPocketBuccal",
  "periodontalPocketDBuc",
  "periodontalPocketMLingPal",
  "periodontalPocketLingPal",
  "periodontalPocketDLingPal",
  "gingivalRecessionMBuc",
  "gingivalRecessionBuccal",
  "gingivalRecessionDBuc",
  "gingivalRecessionMLingPal",
  "gingivalRecessionLingPal",
  "gingivalRecessionDLingPal"
];

const resolveFields = (values: PartialNonNullable<FormValues>) => {
  return fieldsToResolve.reduce((acc, field) => {
    acc[field] = resolveNumber(values[field], true, true);
    return acc;
  }, {} as Record<keyof FormValues, number | null | undefined>);
};

export const useUpdateQuickChart = (
  selection: Charts,
  currentChart: Chart | null
) => {
  const dispatch = useDispatch();
  const { id: caseId } = useParams();

  const [update] = useUpdateQuickChartMutation({ ignoreResults: true });

  const shouldSync = useCurrentCase(data => data.shouldSync);

  const chartIds =
    selection.length === 1
      ? [currentChart?._id || null]
      : selection.map(chart => chart._id || null);
  const chartFakeIds =
    selection.length === 1
      ? [currentChart?.fakeId || null]
      : selection.map(chart => chart.fakeId || null);

  return React.useCallback<FormWatcherCallback<FormValues>>(
    formValues => {
      const resolvedData = resolveFields(formValues);
      const data = omitBy(resolvedData, value => value === undefined);
      const variables = { ...formValues, ...data };

      dispatch(
        casesActions.updateDentalChart({
          _id: caseId,
          data: {
            ids: compact([...chartIds, ...chartFakeIds]),
            ...variables
          } as any
        })
      );

      if (shouldSync) {
        return update({
          variables: { input: { ids: compact(chartIds), ...variables } }
        });
      }
    },
    [update, chartIds, chartFakeIds, shouldSync]
  );
};
